interface ReadModelEntry {
  value: unknown
  storedAtMs: number
}

const values = new Map<string, ReadModelEntry>()
const pending = new Map<string, Promise<unknown>>()
let generation = 0

export const NAVIGATION_READ_REUSE_MS = 10_000
const READ_MODEL_RETENTION_MS = 60_000
const MAX_READ_MODELS = 96

function cloneReadModel<T>(value: T): T {
  return structuredClone(value)
}

function pruneReadModels(nowMs: number): void {
  for (const [key, entry] of values) {
    if (nowMs - entry.storedAtMs > READ_MODEL_RETENTION_MS) values.delete(key)
  }
  while (values.size >= MAX_READ_MODELS) {
    const oldest = values.keys().next()
    if (oldest.done) break
    values.delete(oldest.value)
  }
}

export function currentReadModelGeneration(): number {
  return generation
}

export function cachedReadModel(
  key: string,
  maxAgeMs = NAVIGATION_READ_REUSE_MS,
): { hit: true; value: unknown } | { hit: false } {
  const entry = values.get(key)
  if (!entry) return { hit: false }
  if (Date.now() - entry.storedAtMs > maxAgeMs) {
    values.delete(key)
    return { hit: false }
  }
  return { hit: true, value: cloneReadModel(entry.value) }
}

export function rememberReadModel<T>(
  key: string,
  value: T,
  expectedGeneration = generation,
): T {
  if (expectedGeneration === generation) {
    const nowMs = Date.now()
    pruneReadModels(nowMs)
    values.delete(key)
    values.set(key, { value: cloneReadModel(value), storedAtMs: nowMs })
  }
  return value
}

export function pendingReadModel<T>(key: string): Promise<T> | null {
  const promise = pending.get(key) as Promise<T> | undefined
  return promise ? promise.then((value) => cloneReadModel(value)) : null
}

export function trackReadModel<T>(
  key: string,
  promise: Promise<T>,
): Promise<T> {
  pending.set(key, promise)
  void promise.then(
    () => {
      if (pending.get(key) === promise) pending.delete(key)
    },
    () => {
      if (pending.get(key) === promise) pending.delete(key)
    },
  )
  return promise
}

export function invalidateReadModels(
  predicate?: (key: string) => boolean,
): void {
  generation += 1
  if (!predicate) {
    values.clear()
    pending.clear()
    return
  }
  for (const key of values.keys()) if (predicate(key)) values.delete(key)
  for (const key of pending.keys()) if (predicate(key)) pending.delete(key)
}
